import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Button,
  Avatar,
  CircularProgress,
  IconButton,
  InputAdornment,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Search, Download, Refresh, Delete } from "@mui/icons-material";
import {
  getAdmitCards,
  exportAdmitCardsCSV,
  deleteAdmitCard,
  bulkDeleteAdmitCards,
} from "../services/api";

const VENUES = ["Lahore", "Islamabad", "Faisalabad", "Multan"];

const AdmitCards: React.FC = () => {
  const [admitCards, setAdmitCards] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [venueFilter, setVenueFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getAdmitCards(venueFilter, search);
      setAdmitCards(response.data);
      // Clear selection after data refresh
      setSelectedIds([]);
    } catch (error) {
      console.error("Error fetching admit cards:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueFilter, search]);

  const handleExport = () => {
    exportAdmitCardsCSV(venueFilter);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = admitCards.map((card) => card._id);
      setSelectedIds(newSelected);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string) => {
    const selectedIndex = selectedIds.indexOf(id);
    let newSelected: string[] = [];
    if (selectedIndex === -1) {
      newSelected = [...selectedIds, id];
    } else {
      newSelected = selectedIds.filter((item) => item !== id);
    }

    setSelectedIds(newSelected);
  };

  const openDeleteDialog = (id: string | null = null) => {
    if (id) {
      setDeletingId(id);
      setIsBulkDelete(false);
    } else {
      setIsBulkDelete(true);
    }
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeletingId(null);
    setIsBulkDelete(false);
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      if (isBulkDelete) {
        await bulkDeleteAdmitCards(selectedIds);
      } else if (deletingId) {
        await deleteAdmitCard(deletingId);
      }
      await fetchData();
      closeDeleteDialog();
    } catch (error) {
      console.error("Error deleting admit card(s):", error);
      alert("Failed to delete admit card(s)");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" fontWeight="bold" gutterBottom color="#079560">
        Admit Cards Management
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            select
            label="Filter by Venue"
            value={venueFilter}
            onChange={(e) => setVenueFilter(e.target.value)}
            sx={{ minWidth: 200 }}
            size="small"
          >
            <MenuItem value="">All Venues</MenuItem>
            {VENUES.map((v) => (
              <MenuItem key={v} value={v}>
                {v}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Search Candidate"
            placeholder="Name, Roll No, CNIC..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flexGrow: 1 }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          {selectedIds.length > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<Delete />}
              onClick={() => openDeleteDialog()}
            >
              Delete Selected ({selectedIds.length})
            </Button>
          )}

          <Button
            variant="contained"
            color="success"
            startIcon={<Download />}
            onClick={handleExport}
            sx={{ bgcolor: "#079560", "&:hover": { bgcolor: "#067a4e" } }}
          >
            Export CSV
          </Button>

          <IconButton onClick={fetchData} title="Refresh">
            <Refresh />
          </IconButton>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" p={5}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ bgcolor: "#f5f5f5" }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={
                      admitCards.length > 0 &&
                      selectedIds.length === admitCards.length
                    }
                    indeterminate={
                      selectedIds.length > 0 &&
                      selectedIds.length < admitCards.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Photo</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Test Number</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Roll Number</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>CNIC</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Venue</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {admitCards.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No admit cards found.
                  </TableCell>
                </TableRow>
              ) : (
                admitCards.map((card) => {
                  const isItemSelected = selectedIds.indexOf(card._id) !== -1;
                  return (
                    <TableRow
                      key={card._id}
                      hover
                      onClick={() => handleSelectOne(card._id)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      selected={isItemSelected}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={isItemSelected} />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Avatar
                          src={`https://backend.hunarmandpunjab.org.pk/${card.studentImage}`}
                          variant="rounded"
                          sx={{ width: 50, height: 50 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {card.testNumber}
                      </TableCell>
                      <TableCell>{card.userId?.rollNumber}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {card.userId?.fullName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {card.userId?.fatherName}
                        </Typography>
                      </TableCell>
                      <TableCell>{card.userId?.cnic}</TableCell>
                      <TableCell>{card.venue}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box display="flex" gap={1}>
                          <Button
                            size="small"
                            component="a"
                            href={`https://backend.hunarmandpunjab.org.pk${card.pdfUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<Download />}
                          >
                            PDF
                          </Button>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => openDeleteDialog(card._id)}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            {isBulkDelete
              ? `the ${selectedIds.length} selected admit cards`
              : "this admit card"}
            ? This action cannot be undone and will also remove all associated
            files from the server.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            autoFocus
            disabled={deleteLoading}
            startIcon={
              deleteLoading ? <CircularProgress size={20} /> : <Delete />
            }
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdmitCards;
